import { useNavigate } from "react-router-dom";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../components/Common/AppOverlay";
import {
  annualCycleEndDate,
  cycleDateError,
  cycleStandardIds,
} from "./inspectionCycleValidation";
import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Form,
} from "reactstrap";
import Select from "react-select";
import * as Yup from "yup";
import { useFormik } from "formik";
import { SuccessNotify, ErrorNotify } from "../../shared/Alert";
import * as url from "../../helpers/url_helper";
import { showLoading } from "react-global-loading";
import { getData, postData, postRawData } from "../../helpers/url-request";
import {
  getStandardTypes,
  getReviewTypes,
  saveInspectionCycle,
  getInspectionCycle,
} from "../../helpers/backend_helper";
import { UploaderComponent } from "../../components/Common/LegacyGridAdapter";

// Formik validation

const emptyFormValues = {
  id: 0,
  projectTitle: "",
  standardTypeIds: [],
  reviewTypeId: "",
  startDate: "",
  endDate: "",
  description: "",
  inspectionLeadId: "",
};

const toDateInputValue = (value) => (value ? String(value).slice(0, 10) : "");

const InspectionCycleDetailsForm = (props) => {
  const navigate = useNavigate();
  const [createdCycle, setCreatedCycle] = useState(null);

  const finishCreation = (action) => {
    const response = createdCycle;
    setCreatedCycle(null);
    if (props.onFinishCreation) {
      props.onFinishCreation(response, action);
      return;
    }
    props.closeAction?.(response);
    if (action)
      navigate(
        `/inspections/${response.data.id}/edit?step=engagements&action=${action}`
      );
  };

  const [leadOptions, setLeadOptions] = useState([]);
  const [createdByName, setCreatedByName] = useState("Unavailable");
  const [standardTypeList, setStandardTypeList] = useState([]);
  const [reviewTypeList, setReviewTypeList] = useState([]);
  const [formData, setFormData] = useState(emptyFormValues);
  const [lockedStandardTypeIds, setLockedStandardTypeIds] = useState([]);

  useEffect(() => {
    let isCurrent = true;

    getData("/InspectionCycle/lead-options").then((res) => {
      if (!isCurrent) return;
      if (res?.status) setLeadOptions(Array.isArray(res.data) ? res.data : []);
      else ErrorNotify("Unable to load inspection leads.");
    }).catch(() => { if (isCurrent) ErrorNotify("Unable to load inspection leads."); });

    getStandardTypes()
      .then((res) => {
        if (isCurrent && res.status === true) {
          setStandardTypeList(res.data);
          if (!(props.dataId > 0)) {
            setFormData((current) => ({
              ...current,
              standardTypeIds: (res.data || []).map((standard) => Number(standard.id)),
            }));
          }
        }
      })
      .catch((err) => {
        console.log(err);
        showLoading(false);
        ErrorNotify("Error occured while fetching data");
      });

    getReviewTypes()
      .then((res) => {
        if (isCurrent && res.status === true) {
          setReviewTypeList(res.data || []);
          if (!(props.dataId > 0) && res.data?.length === 1) {
            setFormData((current) => ({
              ...current,
              reviewTypeId: res.data[0].id,
            }));
          }
        }
      })
      .catch((err) => {
        console.log(err);
        showLoading(false);
        ErrorNotify("Error occured while fetching data");
      });

    if (props.dataId > 0) {
      showLoading(true);
      getInspectionCycle(props.dataId)
        .then((res) => {
          if (!isCurrent) return;
          if (res.status === true && res.data) {
            const inspectionCycle = res.data;
            setCreatedByName(inspectionCycle.createdByName || "Unavailable");
            const existingStandardIds = cycleStandardIds(inspectionCycle);
            setLockedStandardTypeIds(existingStandardIds);
            setFormData({
              id: inspectionCycle.id,
              inspectionLeadId: inspectionCycle.inspectionLeadId || "",
              projectTitle: inspectionCycle.title || "",
              standardTypeIds: existingStandardIds,
              reviewTypeId: inspectionCycle.reviewTypeId || "",
              startDate: toDateInputValue(inspectionCycle.startDate),
              endDate: annualCycleEndDate(
                toDateInputValue(inspectionCycle.startDate)
              ),
              description: inspectionCycle.description || "",
            });
          } else {
            ErrorNotify(
              res.message || "Unable to load inspection cycle details."
            );
          }
        })
        .catch(() => {
          if (isCurrent)
            ErrorNotify(
              "Error occurred while loading inspection cycle details."
            );
        })
        .finally(() => {
          if (isCurrent) showLoading(false);
        });
    } else {
      setLockedStandardTypeIds([]);
      setFormData(emptyFormValues);
    }

    return () => {
      isCurrent = false;
    };
  }, [props.dataId]);

  // Form validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      ...formData,
    },
    validateOnChange: false,
    validationSchema: Yup.object({
      projectTitle: Yup.string()
        .trim()
        .required("Please enter an inspection cycle name.")
        .max(200, "Use 200 characters or fewer.")
        .test(
          "unique-name",
          "An inspection cycle with this name already exists for your firm.",
          async function (value) {
            if (!value?.trim()) return true;
            try {
              const response = await getData(
                `/inspectioncycle/nameexists?title=${encodeURIComponent(
                  value.trim()
                )}&excludeId=${Number(this.parent.id) || 0}`
              );
              if (!response.status)
                return this.createError({
                  message:
                    "Unable to validate the cycle name. Please try again.",
                });
              return !response.data;
            } catch {
              return this.createError({
                message: "Unable to validate the cycle name. Please try again.",
              });
            }
          }
        ),
      standardTypeIds: Yup.array()
        .of(Yup.number().integer().positive())
        .min(1, "Select at least one standard."),
      reviewTypeId: Yup.number()
        .integer()
        .positive()
        .required("Select a review type."),
      startDate: Yup.string().test(
        "valid-start-date",
        "Enter a valid start date.",
        function (value) {
          const error = cycleDateError(value, "Start date");
          return error ? this.createError({ message: error }) : true;
        }
      ),
      endDate: Yup.string().test(
        "valid-end-date",
        "Enter a valid end date.",
        function (value) {
          const error = cycleDateError(value, "End date");
          if (error) return this.createError({ message: error });
          if (this.parent.startDate && value < this.parent.startDate)
            return this.createError({
              message: "End date must be on or after the start date.",
            });
          return true;
        }
      ),
    }),
    onSubmit: async (values) => {
      showLoading(true);

      var _data = {
        id: Number(values.id) || 0,
        projectTitle: values.projectTitle.trim(),
        standardTypeIds: [
          ...new Set([
            ...lockedStandardTypeIds,
            ...values.standardTypeIds.map(Number),
          ]),
        ],
        reviewTypeId: Number(values.reviewTypeId),
        startDate: values.startDate,
        endDate: annualCycleEndDate(values.startDate),
        description: values.description,
        inspectionLeadId: values.inspectionLeadId ? Number(values.inspectionLeadId) : null,
      };

      return saveInspectionCycle(_data)
        .then((res) => {
          showLoading(false);
          if (res.status == true) {
            SuccessNotify(
              values.id
                ? "Inspection cycle updated."
                : "Inspection cycle is created."
            );
            validation.resetForm({ values });
            if (!values.id && res.data?.id) {
              const createdResponse = {
                ...res,
                data: {
                  ...res.data,
                  title: res.data.title || values.projectTitle.trim(),
                },
              };
              if (props.onCreated) props.onCreated(createdResponse);
              else setCreatedCycle(createdResponse);
            } else props.closeAction?.(res);
          } else {
            ErrorNotify(res.message);
          }
        })
        .catch((err) => {
          showLoading(false);
          ErrorNotify("Error occured while saving data");
        });
    },
  });

  useEffect(() => {
    props.onDirtyChange?.(validation.dirty);
  }, [validation.dirty, props.onDirtyChange]);

  return (
    <React.Fragment>
      <Col xl={12}>
        <CardBody className="inspection-cycle-form">
          <Form
            className="needs-validation inspection-cycle-form__content"
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
              return false;
            }}
          >
            {!props.hideIntro && (
              <p className="inspection-cycle-form__intro">
                {props.dataId
                  ? "Update the inspection cycle details below."
                  : "Provide the basic information to create an inspection cycle. You can assign engagements, templates and reviewers in the next steps."}
              </p>
            )}
            <section className="inspection-cycle-form__section inspection-cycle-form__section--details">
              <div className="inspection-cycle-form__section-title">
                <span>
                  <i className="bx bx-briefcase-alt-2" aria-hidden="true" />
                  Inspection cycle details
                </span>
              </div>
              <FormGroup className="inspection-cycle-form__field--wide">
                <Label>
                  Title <b>*</b>
                </Label>
                <Input
                  name="projectTitle"
                  type="text"
                  placeholder="Enter inspection cycle title"
                  className="form-control"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.projectTitle || ""}
                  invalid={
                    validation.touched.projectTitle &&
                    validation.errors.projectTitle
                      ? true
                      : false
                  }
                />
                {validation.touched.projectTitle &&
                validation.errors.projectTitle ? (
                  <FormFeedback type="invalid">
                    {validation.errors.projectTitle}
                  </FormFeedback>
                ) : null}
              </FormGroup>
              <FormGroup className="inspection-cycle-form__field--wide">
                <Label>Description</Label>
                <Input
                  name="description"
                  type="textarea"
                  rows="4"
                  maxLength="500"
                  placeholder="Provide a brief description (optional)"
                  className="form-control"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.description || ""}
                  invalid={
                    validation.touched.description &&
                    validation.errors.description
                      ? true
                      : false
                  }
                />
                <div className="inspection-cycle-form__count">
                  {(validation.values.description || "").length} / 500
                </div>
                {validation.touched.description &&
                validation.errors.description ? (
                  <FormFeedback type="invalid">
                    {validation.errors.description}
                  </FormFeedback>
                ) : null}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="cycle-standards">
                  Standards <b>*</b>
                </Label>
                <Select
                  inputId="cycle-standards"
                  name="standardTypeIds"
                  isMulti
                  isSearchable
                  closeMenuOnSelect={false}
                  placeholder="Search and select standards"
                  options={standardTypeList.map((standard) => ({
                    value: standard.id,
                    label: standard.name,
                    isLocked: lockedStandardTypeIds.includes(
                      Number(standard.id)
                    ),
                  }))}
                  value={standardTypeList
                    .filter((standard) =>
                      validation.values.standardTypeIds.includes(
                        Number(standard.id)
                      )
                    )
                    .map((standard) => ({
                      value: standard.id,
                      label: standard.name,
                      isLocked: lockedStandardTypeIds.includes(
                        Number(standard.id)
                      ),
                    }))}
                  isOptionDisabled={(option) => option.isLocked}
                  formatOptionLabel={(option) => (
                    <span>
                      {option.label}
                      {option.isLocked && (
                        <small className="inspection-cycle-form__standard-lock">
                          Locked
                        </small>
                      )}
                    </span>
                  )}
                  onChange={(selected) =>
                    validation.setFieldValue("standardTypeIds", [
                      ...new Set([
                        ...lockedStandardTypeIds,
                        ...(selected || []).map((option) =>
                          Number(option.value)
                        ),
                      ]),
                    ])
                  }
                  onBlur={() =>
                    validation.setFieldTouched("standardTypeIds", true)
                  }
                  aria-invalid={Boolean(
                    validation.touched.standardTypeIds &&
                      validation.errors.standardTypeIds
                  )}
                  aria-describedby="cycle-standards-error"
                />
                {lockedStandardTypeIds.length > 0 && (
                  <small className="inspection-cycle-form__locked-help">
                    <i className="bx bx-lock-alt" /> Existing standards are
                    locked. You can add standards, but you cannot remove
                    standards already attached to this cycle.
                  </small>
                )}
                {validation.touched.standardTypeIds &&
                  validation.errors.standardTypeIds && (
                    <div
                      id="cycle-standards-error"
                      className="invalid-feedback d-block"
                    >
                      {validation.errors.standardTypeIds}
                    </div>
                  )}
              </FormGroup>
              {props.dataId > 0 && (
                <FormGroup>
                  <Label for="cycleCreatedBy">Created By</Label>
                  <Input id="cycleCreatedBy" value={createdByName} readOnly />
                </FormGroup>
              )}
              <FormGroup>
                <Label for="inspectionLeadId">Inspection Lead</Label>
                <Input id="inspectionLeadId" name="inspectionLeadId" type="select"
                  value={validation.values.inspectionLeadId}
                  onChange={validation.handleChange}>
                  <option value="">Not assigned</option>
                  {validation.values.inspectionLeadId && !leadOptions.some(user => Number(user.id) === Number(validation.values.inspectionLeadId)) && (
                    <option value={validation.values.inspectionLeadId}>Current lead (unavailable for assignment)</option>
                  )}
                  {leadOptions.map(user => <option key={user.id} value={user.id}>{user.fullName}</option>)}
                </Input>
              </FormGroup>
              <FormGroup>
                <Label>
                  Review Type <b>*</b>
                </Label>
                <Input
                  type="select"
                  className="form-select"
                  name="reviewTypeId"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.reviewTypeId || ""}
                  invalid={Boolean(
                    validation.touched.reviewTypeId &&
                      validation.errors.reviewTypeId
                  )}
                >
                  <option value="">Select Review Type</option>
                  {reviewTypeList.map((type) => (
                    <option value={type.id} key={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Input>
                {validation.touched.reviewTypeId &&
                validation.errors.reviewTypeId ? (
                  <FormFeedback type="invalid">
                    {validation.errors.reviewTypeId}
                  </FormFeedback>
                ) : null}
              </FormGroup>
              <FormGroup>
                <Label>
                  Start Date <b>*</b>
                </Label>
                <Input
                  name="startDate"
                  type="date"
                  className="form-control"
                  onChange={(event) => {
                    const startDate = event.target.value;
                    validation.setValues((current) => ({
                      ...current,
                      startDate,
                      endDate: annualCycleEndDate(startDate),
                    }));
                  }}
                  onBlur={validation.handleBlur}
                  value={validation.values.startDate || ""}
                  invalid={Boolean(
                    validation.touched.startDate && validation.errors.startDate
                  )}
                />
                {validation.touched.startDate && validation.errors.startDate ? (
                  <FormFeedback type="invalid">
                    {validation.errors.startDate}
                  </FormFeedback>
                ) : null}
              </FormGroup>
              <FormGroup>
                <Label>
                  End Date <b>*</b>
                </Label>
                <Input
                  name="endDate"
                  type="date"
                  className="form-control"
                  min={validation.values.startDate || undefined}
                  readOnly
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.endDate || ""}
                  invalid={Boolean(
                    validation.touched.endDate && validation.errors.endDate
                  )}
                />
                <small className="text-muted">
                  Automatically set for an annual inspection cycle.
                </small>
                {validation.touched.endDate && validation.errors.endDate ? (
                  <FormFeedback type="invalid">
                    {validation.errors.endDate}
                  </FormFeedback>
                ) : null}
              </FormGroup>
            </section>
            <div className="inspection-cycle-drawer-actions">
              <Button
                className="inspection-cycle-drawer-actions__close"
                type="button"
                onClick={props.onRequestClose || props.closeAction}
              >
                Cancel
              </Button>
              <Button
                className="inspection-cycle-drawer-actions__submit"
                type="submit"
                disabled={validation.isSubmitting}
              >
                {props.submitLabel ||
                  (props.dataId ? "Save Changes" : "Create Inspection Cycle")}
              </Button>
            </div>
          </Form>
        </CardBody>
      </Col>
      <Modal
        isOpen={Boolean(createdCycle)}
        backdrop="static"
        toggle={() => finishCreation(null)}
      >
        <ModalHeader toggle={() => finishCreation(null)}>
          Inspection cycle created
        </ModalHeader>
        <ModalBody>
          Would you like to import or create engagements for{" "}
          <strong>{createdCycle?.data?.title}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            color="secondary"
            onClick={() => finishCreation(null)}
          >
            Later
          </Button>
          <Button
            type="button"
            color="primary"
            outline
            onClick={() => finishCreation("import")}
          >
            Import Engagements
          </Button>
          <Button
            type="button"
            color="primary"
            onClick={() => finishCreation("create")}
          >
            Create Engagement
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default InspectionCycleDetailsForm;
